/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    type ComponentPropsWithoutRef,
    type KeyboardEvent as ReactKeyboardEvent,
    type ReactElement,
} from 'react';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, {
    WithCategoryNavigationMenu,
    type CategoryNavigationMenuListCtx,
    type CategoryNavigationMenuListItemCtx,
} from '@/components/navigation-menu';
import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Icon } from '@/components/icon';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useSubCategory } from '@/components/navigation-menu/context';
import { routes, routeHref } from '@/route-paths';
import { PLACEHOLDER_ROOT_CATEGORIES } from './placeholder-categories';

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

function hasLevel3Children(category: ShopperProducts.schemas['Category']): boolean {
    return (
        typeof category.onlineSubCategoriesCount === 'number' &&
        category.onlineSubCategoriesCount > 0 &&
        Array.isArray(category.categories) &&
        category.categories.length > 0
    );
}

/**
 * Resolves which Level 2 category is "active" in the mega menu rail: the caller's current
 * selection if it's still one of this Level 1 item's children, otherwise the first Level 2
 * category (the Figma-specified default, see `site-header` spec "Desktop mega menu renders
 * three category levels with a selectable rail"). Falling back like this — rather than an
 * explicit reset — is what makes switching Level 1 items land on a sensible default without
 * extra state plumbing (see decision 2a in design.md).
 */
function resolveActiveLevel2Id(
    level2Categories: ShopperProducts.schemas['Category'][],
    activeId: string | undefined
): string | undefined {
    if (activeId && level2Categories.some((c) => c.id === activeId)) {
        return activeId;
    }
    return level2Categories[0]?.id;
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const imageSrc = toImageUrl({ src: (category?.c_slotBannerImage as string) ?? '', config });

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={routeHref(routes.category, { categoryId: category.id })}>
                {imageSrc ? (
                    <img
                        className="object-contain w-full max-w-full max-h-[512px]"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // eslint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

const SUBCATEGORY_LINK_CLASS =
    'text-sm font-medium leading-5 hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/60 focus:!text-header-menu-foreground/60 transition-colors';

const FOCUS_VISIBLE_RING_CLASS =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Level 2 rail entry in the desktop mega menu (Figma node `12938:52956`). Categories with their
 * own Level 3 children render as a non-navigating, hover- or explicitly keyboard-activatable rail item that
 * swaps the Level 3 panel via `onActivate` — clicking through happens via the panel's
 * "Shop All [Category]" link, not the rail item itself. Categories without Level 3 children stay
 * plain navigable links, matching the previous (and still current) leaf treatment.
 */
function MegaMenuLevel2RailItem({
    category,
    isActive,
    onActivate,
    onTabIntoPanel,
}: {
    category: ShopperProducts.schemas['Category'];
    isActive: boolean;
    onActivate: () => void;
    onTabIntoPanel: () => void;
}): ReactElement {
    if (!hasLevel3Children(category)) {
        return (
            <NavigationMenuLink asChild>
                <NavLink
                    to={routeHref(routes.category, { categoryId: category.id })}
                    className={SUBCATEGORY_LINK_CLASS}>
                    {category.name}
                </NavLink>
            </NavigationMenuLink>
        );
    }

    return (
        <button
            type="button"
            onMouseEnter={onActivate}
            onClick={onActivate}
            onKeyDown={(event) => {
                if (event.key === 'Tab' && !event.shiftKey) {
                    event.preventDefault();
                    onTabIntoPanel();
                }
            }}
            data-mega-menu-level2-id={category.id}
            aria-current={isActive ? 'true' : undefined}
            className={cn(
                'flex w-full items-center justify-between gap-2 rounded-ui px-3 py-2.5 text-left text-sm transition-colors',
                FOCUS_VISIBLE_RING_CLASS,
                // font-bold (700) matches the "Shop All [Category]" link's weight in the panel this
                // rail item controls — the active state should read as equally emphasized.
                isActive
                    ? 'font-bold bg-header-menu-active-background text-header-menu-foreground'
                    : 'font-medium hover:bg-header-menu-hover-background'
            )}>
            {category.name}
            <ChevronRight className="size-4 shrink-0 opacity-60" aria-hidden="true" />
        </button>
    );
}

/**
 * Right-hand panel of the desktop mega menu: the active Level 2 category's "Shop All [Category]"
 * PLP link plus a grid of its Level 3 children. `category.categories` is already the enriched
 * Level 3 data — `WithCategoryNavigationMenu`'s `defer` promise fills the sub-category store keyed
 * by Level 1 id, so Level 2 objects arrive with their own children embedded; no extra store lookup
 * needed here.
 */
function MegaMenuLevel3Panel({
    category,
    onTabToNextLevel2,
}: {
    category: ShopperProducts.schemas['Category'];
    onTabToNextLevel2: () => boolean;
}): ReactElement {
    const { t } = useTranslation('header');
    const level3Categories = category.categories ?? [];
    const handleLastPanelItemKeyDown = (event: ReactKeyboardEvent<HTMLAnchorElement>) => {
        if (event.key === 'Tab' && !event.shiftKey && onTabToNextLevel2()) {
            event.preventDefault();
        }
    };

    return (
        <div id={`mega-menu-panel-${category.id}`} className="flex-1 min-w-0 p-4">
            <NavigationMenuLink asChild>
                <NavLink
                    to={routeHref(routes.category, { categoryId: category.id })}
                    onKeyDown={level3Categories.length === 0 ? handleLastPanelItemKeyDown : undefined}
                    className={cn(
                        'inline-block pb-3 text-base font-bold hover:opacity-70 transition-opacity',
                        FOCUS_VISIBLE_RING_CLASS
                    )}>
                    {t('shopAllCategory', { category: category.name, defaultValue: `Shop All ${category.name}` })}
                </NavLink>
            </NavigationMenuLink>
            {level3Categories.length > 0 && (
                <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 border-t border-header-menu-border pt-4">
                    {level3Categories.map((child) => (
                        <li key={child.id}>
                            <NavigationMenuLink asChild>
                                <NavLink
                                    to={routeHref(routes.category, { categoryId: child.id })}
                                    onKeyDown={
                                        child.id === level3Categories.at(-1)?.id
                                            ? handleLastPanelItemKeyDown
                                            : undefined
                                    }
                                    className={cn(
                                        'block rounded-ui',
                                        SUBCATEGORY_LINK_CLASS,
                                        FOCUS_VISIBLE_RING_CLASS
                                    )}>
                                    {child.name}
                                </NavLink>
                            </NavigationMenuLink>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    return (
        typeof category.onlineSubCategoriesCount === 'number' &&
        category.onlineSubCategoriesCount > 0 &&
        Array.isArray(category.categories) &&
        category.categories.length > 0
    );
}

/**
 * One row of the mobile/tablet nav drawer, recursive to arbitrary depth (Figma nodes `6215:82254`,
 * `25610:119344`, `25610:124116`). Every level — not just the top one — gets its own independent
 * expand/collapse toggle (all default closed), and each expanded level's children render in a
 * `pl-4 border-l` list, so indentation and the left-hand vertical line compound naturally with
 * depth purely from nesting: no per-level styling math needed.
 *
 * The one shape that isn't uniform across levels: a bold "Shop All [Category]" link is prepended
 * to a category's children — but only when that category is itself *not* a top-level (Level 1)
 * item. Level 1 expanding into Level 2 has no such link (matches Figma); Level 2 expanding into
 * Level 3 does (mirrors the desktop mega menu's "Shop All" PLP link for the same category).
 */
function MobileMenuCategory({
    category: rawCategory,
    expandedCategories,
    onToggle,
    onNavigate,
    isTopLevel = false,
}: {
    category: ShopperProducts.schemas['Category'];
    expandedCategories: Set<string>;
    onToggle: (categoryId: string) => void;
    onNavigate: () => void;
    isTopLevel?: boolean;
}): ReactElement {
    const { t } = useTranslation('header');
    const enrichedCategory = useSubCategory(rawCategory.id);
    const category = enrichedCategory ?? rawCategory;
    const hasChildren = hasSubcategories(category);
    const isExpanded = expandedCategories.has(category.id);

    return (
        <li>
            <div className="flex items-center justify-between">
                <NavLink
                    to={routeHref(routes.category, { categoryId: category.id })}
                    onClick={onNavigate}
                    className={cn(
                        'flex-1 py-3 text-base font-medium hover:opacity-70 transition-opacity rounded-ui',
                        FOCUS_VISIBLE_RING_CLASS
                    )}>
                    {category.name}
                </NavLink>

                {hasChildren && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggle(category.id)}
                        className="ml-2 p-2 h-auto shrink-0 hover:bg-transparent hover:opacity-50 transition-opacity"
                        aria-label={
                            isExpanded
                                ? t('collapseCategory', {
                                      category: category.name,
                                      defaultValue: `Collapse ${category.name}`,
                                  })
                                : t('expandCategory', {
                                      category: category.name,
                                      defaultValue: `Expand ${category.name}`,
                                  })
                        }
                        aria-expanded={isExpanded}>
                        <ChevronDown
                            className={cn('size-5 transition-transform duration-200', {
                                'rotate-180': isExpanded,
                            })}
                        />
                    </Button>
                )}
            </div>

            {hasChildren && isExpanded && (
                <ul className="pl-4 pb-2 space-y-1 border-l border-header-foreground/10">
                    {!isTopLevel && (
                        <li>
                            <NavLink
                                to={routeHref(routes.category, { categoryId: category.id })}
                                onClick={onNavigate}
                                className={cn(
                                    'block py-2 text-sm font-bold hover:opacity-70 transition-opacity rounded-ui',
                                    FOCUS_VISIBLE_RING_CLASS
                                )}>
                                {t('shopAllCategory', {
                                    category: category.name,
                                    defaultValue: `Shop All ${category.name}`,
                                })}
                            </NavLink>
                        </li>
                    )}
                    {category.categories?.map((subCategory) => (
                        <MobileMenuCategory
                            key={subCategory.id}
                            category={subCategory}
                            expandedCategories={expandedCategories}
                            onToggle={onToggle}
                            onNavigate={onNavigate}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

/**
 * MobileMenuDropdown - Renders the mobile/tablet nav as a right-side drawer (Figma `6215:82254`).
 * Built on the same `Sheet` primitive as `cart-sheet.tsx` — full viewport height, up to 400px wide
 * (narrower on smaller screens via `w-full max-w-[400px]`), its own scroll region so the page
 * behind it stays locked, closes on outside click/Escape (Radix `Dialog` defaults), and includes
 * `Sheet`'s built-in close control.
 */
export function MobileMenuDropdown(): ReactElement | null {
    const context = useMobileMenu();
    const { t } = useTranslation('header');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!context) {
            return;
        }
        const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const closeOnDesktop = (event: MediaQueryListEvent) => {
            if (event.matches) {
                context.close();
            }
        };

        // A `md:hidden` class can hide the portaled sheet content after rotation, but it cannot
        // change Radix's open state. Close the controlled Sheet at the breakpoint so its overlay,
        // focus trap, and scroll lock are all removed with the drawer.
        if (desktopMediaQuery.matches) {
            context.close();
        }
        desktopMediaQuery.addEventListener('change', closeOnDesktop);
        return () => desktopMediaQuery.removeEventListener('change', closeOnDesktop);
    }, [context]);

    if (!context) {
        return null;
    }

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    return (
        <Sheet
            open={context.isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    context.close();
                }
            }}>
            {/* Mount the category list only while the menu is open. Each MobileMenuCategory subscribes to the
                sub-category store via useSubCategory (useSyncExternalStore). Keeping that subtree mounted while the
                menu is closed puts the subscribers in the SSR/hydration tree, so the post-hydration store fill
                re-renders the header and cascades into a whole-page flicker. Gating the mount on `isOpen` keeps the
                subscribers out of the initial render — matching the desktop mega panel, whose subscribers live
                inside the lazily-mounted Radix content. */}
            {context.isOpen && (
                <SheetContent
                    side="right"
                    showClose={false}
                    className="md:hidden w-full max-w-[400px] p-0 gap-0 flex flex-col">
                    <SheetTitle className="sr-only">{t('mobileNavigation', 'Mobile navigation menu')}</SheetTitle>
                    {/* Own close control (showClose={false} above suppresses the default one) styled for
                        legibility over scrolling content underneath it — opaque background, solid foreground
                        color — rather than the default `SheetContent` close, which is small and semi-transparent.
                        Positioned absolutely against `SheetContent` itself (not the scrollable `<nav>` below) so
                        it stays fixed in place while the category list scrolls. */}
                    <SheetClose
                        className={cn(
                            'absolute top-0 right-0 z-10 flex size-12 items-center justify-center',
                            'bg-background text-foreground opacity-100 transition-opacity hover:opacity-70',
                            FOCUS_VISIBLE_RING_CLASS
                        )}>
                        <Icon name="close" />
                        <span className="sr-only">{t('closeMenu', 'Close menu')}</span>
                    </SheetClose>
                    {/* pt-12 reserves room below the close control above so the first nav item isn't crowded by it. */}
                    <nav
                        className="flex-1 overflow-y-auto px-4 pt-12 pb-4"
                        aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                        <ul className="space-y-1">
                            {context.categories.map((category) => (
                                <MobileMenuCategory
                                    key={category.id}
                                    category={category}
                                    expandedCategories={expandedCategories}
                                    onToggle={toggleCategory}
                                    onNavigate={context.close}
                                    isTopLevel
                                />
                            ))}
                        </ul>
                    </nav>
                </SheetContent>
            )}
        </Sheet>
    );
}

/**
 * NavigationContent - The actual nav UI (hamburger + mega menu), parameterized purely by
 * `categories`. Split out from `ResponsiveNavigationMenu` so the exact same component backs both
 * the happy path (resolved catalog data) and the two placeholder-data fallbacks required by the
 * `site-header` spec's "Missing catalog data falls back to placeholder" scenario: an empty
 * resolved category list, and a rejected root-category promise (`errorElement`, below).
 */
function NavigationContent({ categories }: { categories: ShopperProducts.schemas['Category'][] }): ReactElement {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    // Which Level 2 category is active in the desktop mega menu's right panel. A single piece of
    // state suffices — only one Level 1 item's mega menu can be open at a time — and
    // `resolveActiveLevel2Id` falls back to the first Level 2 child whenever this id doesn't
    // belong to the currently-open Level 1 item's children (e.g. right after switching Level 1
    // items), so no explicit reset wiring is needed.
    const [activeLevel2Id, setActiveLevel2Id] = useState<string | undefined>(undefined);
    const { t } = useTranslation('header');
    const navigate = useNavigate();

    const defaultListStyle = {
        width: '100%',
        maxWidth: '100%',
    };

    // Handler for top-level category clicks
    const handleTopLevelClick = useCallback(
        (categoryId: string) => {
            void navigate(routeHref(routes.category, { categoryId }));
        },
        [navigate]
    );

    // Element props generator
    const getElementProps = useCallback(
        ({
            level,
            category,
            isLeaf,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            isLeaf?: boolean;
        }) => {
            const isSubcategory = level >= 1;
            const isClickableParent = level === 0 && !isLeaf && category.id;

            return {
                className: cn(
                    'text-sm font-medium leading-5',
                    // Level 1 items sit in a row that's already tight at the md/lg tiers (see the
                    // `site-header` spec's responsive tier requirement) — without this, a longer
                    // label like "Dealer Support" wraps to two lines instead of shrinking its
                    // neighbors' space.
                    level === 0 && 'whitespace-nowrap shrink-0',
                    isSubcategory &&
                        'hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/60 focus:!text-header-menu-foreground/60 transition-colors'
                ),
                ...(level === 1 && { 'data-mega-menu-level2-id': category.id }),
                ...(isClickableParent && {
                    // Use onPointerDown instead of onClick for mouse-only navigation.
                    // This preserves keyboard accessibility: Enter/Space on the trigger
                    // expands the dropdown (Radix behavior), while mouse clicks navigate
                    // to the category page. Without this guard, keyboard users would be
                    // forced to navigate without being able to explore subcategories.
                    onPointerDown: (e: React.PointerEvent) => {
                        if (e.pointerType === 'mouse') {
                            handleTopLevelClick(category.id);
                        }
                    },
                }),
            };
        },
        [handleTopLevelClick]
    );

    const focusLevel3Panel = useCallback((categoryId: string) => {
        setActiveLevel2Id(categoryId);
        requestAnimationFrame(() => {
            document.getElementById(`mega-menu-panel-${categoryId}`)?.querySelector<HTMLElement>('a, button')?.focus();
        });
    }, []);

    const focusNextLevel2 = useCallback(
        (level2Categories: ShopperProducts.schemas['Category'][], currentCategoryId: string) => {
            const currentIndex = level2Categories.findIndex((category) => category.id === currentCategoryId);
            const nextCategory = level2Categories[currentIndex + 1];
            if (!nextCategory) {
                return false;
            }

            setActiveLevel2Id(nextCategory.id);
            requestAnimationFrame(() => {
                Array.from(document.querySelectorAll<HTMLElement>('[data-mega-menu-level2-id]'))
                    .find((element) => element.dataset.megaMenuLevel2Id === nextCategory.id)
                    ?.focus();
            });
            return true;
        },
        []
    );

    // Custom element rendering, layered on top of the generic engine's default leaf/trigger
    // rendering (see navigation-menu/impl.tsx): Level 1 trigger labels stay the plain category
    // name (preserving the previous default); Level 2 categories that have their own Level 3
    // children become non-navigating rail buttons that drive `activeLevel2Id`. Everything else
    // (childless Level 2 items, Level 3 links, and any category inside a `c_headerMenuBanner`
    // category — an existing, unrelated feature this change doesn't touch) falls through to the
    // engine's default leaf-link rendering by returning `undefined`.
    const renderElement = useCallback(
        (itemCtx: CategoryNavigationMenuListItemCtx) => {
            if (itemCtx.level === 0 && !itemCtx.isLeaf) {
                return itemCtx.category.name;
            }
            const parentCategory = itemCtx.parent;
            // `Boolean(...)` (rather than using the `hasBanner(...)` guard's result directly)
            // deliberately avoids TS's aliased-condition narrowing: `hasBanner` is a type guard on
            // `parentCategory`, and narrowing it to `undefined` here would make `parentCategory`
            // unusable below even via optional chaining.
            const parentHasBanner: boolean = Boolean(hasBanner(parentCategory));
            if (itemCtx.level === 1 && !parentHasBanner && hasLevel3Children(itemCtx.category)) {
                const level2Categories = parentCategory?.categories ?? [];
                return (
                    <MegaMenuLevel2RailItem
                        category={itemCtx.category}
                        isActive={itemCtx.category.id === resolveActiveLevel2Id(level2Categories, activeLevel2Id)}
                        onActivate={() => setActiveLevel2Id(itemCtx.category.id)}
                        onTabIntoPanel={() => focusLevel3Panel(itemCtx.category.id)}
                    />
                );
            }
            return undefined;
        },
        [activeLevel2Id, focusLevel3Panel]
    );

    // maxDepth={2}: the generic engine only auto-recurses into Level 3 when a Level 2 item is
    // treated as a "branch" (see navigation-menu/impl.tsx). Capping depth at 2 makes every Level 2
    // item a "leaf" as far as the engine's own recursion is concerned, so it never auto-renders
    // every Level 2 category's Level 3 children at once (the flat-list bug confirmed by task 1.6).
    // `renderSlotListAfter` below renders the Level 3 panel for only the active Level 2 category,
    // using the same source data (`ctx.categories`) instead of the engine's recursion.
    const megaMenuMaxDepth = 2;

    const mobileMenuContext: MobileMenuContextType = {
        isOpen: mobileMenuOpen,
        toggle: () => setMobileMenuOpen(!mobileMenuOpen),
        close: () => setMobileMenuOpen(false),
        categories,
    };

    return (
        <MobileMenuContext.Provider value={mobileMenuContext}>
            {/* Mobile/tablet: Hamburger button (below md — mega menu doesn't fit at tablet widths either).
                `size="icon-md"` is a fixed 40x40 box — matches the wishlist/cart/account siblings and
                sidesteps the base `size` variant's `has-[>:is(svg,[data-slot=icon])]:px-4`, whose
                `:has()` selector has higher specificity than a plain `px-*` override could ever beat. */}
            <Button
                variant="ghost"
                size="icon-md"
                onClick={mobileMenuContext.toggle}
                className="md:hidden hover:bg-transparent hover:opacity-50 transition-opacity"
                aria-label={mobileMenuOpen ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu')}
                aria-expanded={mobileMenuOpen}>
                {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>

            {/* Desktop: Mega menu (always rendered, hidden below md with CSS) */}
            <div className="hidden md:flex items-center h-full">
                <CategoryNavigationMenu
                    categories={categories}
                    maxDepth={megaMenuMaxDepth}
                    delayDuration={0}
                    propsViewport={() => ({
                        className:
                            ' border-0 shadow-lg [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none will-change-transform',
                        // Anchor the fixed panel to both viewport edges via `left: 0` + `right: 0`
                        // so its width matches the layout viewport, *excluding* the scrollbar gutter.
                        // Using `width: 100vw` instead would include the scrollbar and overshoot
                        // any in-flow content (e.g. the header) by the scrollbar's width.
                        style: {
                            position: 'fixed',
                            top: 'var(--header-height)',
                            left: 0,
                            right: 0,
                        },
                    })}
                    propsContentContainer={() => ({
                        className: '!p-0 !left-auto !right-auto !w-full md:!w-full !animate-none !transition-none',
                    })}
                    propsContent={({ category }) => ({
                        className: cn(
                            'section-container',
                            hasBanner(category)
                                ? isVertical(category)
                                    ? 'grid md:grid-cols-[1fr_.3fr] items-start'
                                    : 'grid md:grid-cols-[1fr_.6fr] items-start'
                                : // Rail and panel each carry their own 16px padding (`p-4`, see
                                  // `propsList`'s rail className and `MegaMenuLevel3Panel`) plus a
                                  // shared full-height divider between them, so no extra gap here —
                                  // `items-stretch` (the flex default) is what makes that divider
                                  // span the taller of the two columns.
                                  'flex flex-col md:flex-row items-stretch'
                        ),
                    })}
                    propsList={({ parent, categories: subCategories, level }) => {
                        if (level === 1) {
                            if (hasBanner(parent)) {
                                if (isVertical(parent)) {
                                    return {
                                        style: defaultListStyle,
                                        className: 'flex flex-col gap-0 p-0',
                                    };
                                }
                                return {
                                    style: {
                                        ...defaultListStyle,
                                        gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                                    },
                                    className: 'grid p-0',
                                };
                            }
                            // Level 2 rail (Figma `12938:52956`): fixed-width vertical list, its
                            // own 16px padding, and a full-height divider against the Level 3
                            // panel. The active category's Level 3 children render alongside it,
                            // via `renderSlotListAfter` below, not as nested list markup.
                            return {
                                className:
                                    'flex flex-col gap-0.5 p-4 w-full md:w-64 shrink-0 md:border-r md:border-header-menu-border',
                            };
                        }
                    }}
                    propsElement={getElementProps}
                    renderElement={renderElement}
                    renderSlotListAfter={(ctx: CategoryNavigationMenuListCtx) => {
                        const { level, parent, categories: level2Categories } = ctx;
                        if (level !== 1) {
                            return undefined;
                        }
                        if (hasBanner(parent)) {
                            return (
                                <aside className="self-stretch">
                                    <CategoryBanner category={parent} />
                                </aside>
                            );
                        }
                        const activeId = resolveActiveLevel2Id(level2Categories, activeLevel2Id);
                        const activeCategory = level2Categories.find((c) => c.id === activeId);
                        if (!activeCategory || !hasLevel3Children(activeCategory)) {
                            return undefined;
                        }
                        return (
                            <MegaMenuLevel3Panel
                                category={activeCategory}
                                onTabToNextLevel2={() => focusNextLevel2(level2Categories, activeCategory.id)}
                            />
                        );
                    }}
                />
            </div>

            {/* Mobile/tablet: Menu dropdown (rendered here to be inside provider) */}
            <MobileMenuDropdown />
        </MobileMenuContext.Provider>
    );
}

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for mobile, tablet, and desktop:
 * - Mobile/tablet (< md, below 768px): Hamburger button with expandable vertical menu
 * - Desktop (>= md, 768px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility. Root-category data that fails to load, resolves empty, or
 * isn't configured yet falls back to `PLACEHOLDER_ROOT_CATEGORIES` (`site-header` spec, "Missing
 * catalog data falls back to placeholder") rather than rendering an empty or broken header.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
}: ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu>): ReactElement {
    return (
        <WithCategoryNavigationMenu
            resolve={resolve}
            defer={defer}
            errorElement={<NavigationContent categories={PLACEHOLDER_ROOT_CATEGORIES} />}>
            {({ categories }) => (
                <NavigationContent categories={categories.length > 0 ? categories : PLACEHOLDER_ROOT_CATEGORIES} />
            )}
        </WithCategoryNavigationMenu>
    );
}
